import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiLavandierService } from '../../../../lib/lib-ngx/web-services/api-lavandier.service';
import { ConfirmationModalComponent } from '../../modals/confirmation-modal/confirmation-modal.component';
import { ListToMap } from '../../../../lib/lib-ngx/utils/ListToMap';
import { AuthService } from '../../../../lib/lib-ngx/web-services/auth.service';
import { UserTypeOrderType } from '../../../../lib/lib-shared/types/UserTypeOrderType';
import { ANONYMISE_DELAY_DAYS, PRICEMULTIPLIER } from '../../../../lib/lib-shared/defines';
import { IOrder } from '../../../../lib/lib-shared/models/Order';
import { IHouseDetailsList } from '../../../../lib/lib-shared/models/House';
import { IKitOrderPayload } from './user-profile-kit-batch/user-profile-kit-batch.types';
import { PickupService } from '../../../services/pickup.service';
import { ErrorService } from '../../../../lib/lib-ngx/services/error.service';

@Component({
  selector: 'lm-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
  public PRICEMULTIPLIER = PRICEMULTIPLIER;
  public UserTypeOrderType = UserTypeOrderType;

  public userId = null;
  public userProfileData: any = null;
  public activeTab = 'user';
  public kitBatch = false;
  public kit = false;
  public userTypeMap = new Map();
  public lavandierMap = new Map();
  public houseList: IHouseDetailsList[] = [];
  public houseMap: Map<number, IHouseDetailsList> = new Map();

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiLavandierService: ApiLavandierService,
    private modalService: NgbModal,
    public authService: AuthService,
    private router: Router,
    private pickupService: PickupService,
    private errorService: ErrorService,
  ) {
  }

  ngOnInit() {
    this.kitBatch = false;
    this.kit = false;
    this.activatedRoute.params.subscribe(params => {
        if (!/^\d+$/.test(params.id)) {
          this.errorService.showModal('Identifiant utilisateur invalide.');
          this.router.navigate(['/user/list']);
          return;
        }
        this.userId = params.id;
        this.loadData();
      }
    );
  }

  loadData() {
    return forkJoin([
      this.apiLavandierService.getUser(this.userId),
      this.apiLavandierService.getHouseUserId(this.userId, {}),
      this.apiLavandierService.getUserTypeList({}),
      this.apiLavandierService.getLavandiers(),
    ])
      .pipe(
        map(([user, houseList, userTypeList, lavandierList]) => {
          return [
            user as any,
            houseList,
            ListToMap.convert(houseList),
            ListToMap.convert(userTypeList),
            ListToMap.convert(lavandierList.rows),
          ];
        })
      )
      .subscribe(([user, houseList, houseMap, userTypeMap, lavandierMap]) => {
        this.userProfileData = user;
        this.houseList = houseList;
        this.houseMap = houseMap;
        this.userTypeMap = userTypeMap;
        this.lavandierMap = lavandierMap;
        this.pickupService.loadPickupDetailsForHouseList(houseList);
      });
  }

  onFreezeAccount() {
    const confirmationModal = this.modalService.open(ConfirmationModalComponent, {size: 'lg'});
    if (!this.userProfileData.enabled) {
      confirmationModal.componentInstance.confirmationDesc = 'Voulez-vous vraiment dégeler le compte de cet utilisateur ?';
      confirmationModal.componentInstance.validateButtonText = 'Dégeler le compte';
    } else {
      confirmationModal.componentInstance.confirmationDesc = 'Voulez-vous vraiment geler le compte de cet utilisateur ?';
      confirmationModal.componentInstance.validateButtonText = 'Geler le compte';
    }
    confirmationModal.componentInstance.cancelButtonText = 'Annuler';
    confirmationModal.result
      .then((result) => {
        if (result) {
          this.apiLavandierService.putUserIdEnabled(this.userProfileData.id, {enabled: !this.userProfileData.enabled})
            .subscribe(() => this.ngOnInit());
        }
      });
  }

  onResetPassword() {
    const confirmationModal = this.modalService.open(ConfirmationModalComponent, {size: 'lg'});
    const name = `${this.userProfileData.firstName} ${this.userProfileData.lastName}`;
    confirmationModal.componentInstance.confirmationDesc = `Voulez-vous vraiment envoyer un email de réinitialisation de mot de passe à ${name} (${this.userProfileData.email}) ? L'utilisateur choisira lui-même son nouveau mot de passe via le lien reçu.`;
    confirmationModal.componentInstance.validateButtonText = 'Envoyer l\'email';
    confirmationModal.componentInstance.cancelButtonText = 'Annuler';
    confirmationModal.result
      .then(result => {
        if (result) {
          this.apiLavandierService.putUserIdForgottenPassword(this.userProfileData.id, {})
            .subscribe();
        }
      })
      .catch(() => null);
  }

  onDeleteAccount() {
    const confirmationModal = this.modalService.open(ConfirmationModalComponent, {size: 'lg'});
    const name = `${this.userProfileData.firstName} ${this.userProfileData.lastName}`;
    confirmationModal.componentInstance.confirmationDesc = `Voulez-vous vraiment demander la suppression du compte de ${name} (${this.userProfileData.email}) ? Les données personnelles de l'utilisateur seront anonymisées de façon irréversible dans ${ANONYMISE_DELAY_DAYS} jours. D'ici là, le compte reste utilisable et la demande peut être annulée.`;
    confirmationModal.componentInstance.validateButtonText = 'Supprimer le compte';
    confirmationModal.componentInstance.cancelButtonText = 'Annuler';
    confirmationModal.result
      .then(result => {
        if (result) {
          this.apiLavandierService.deleteUser(this.userProfileData.id, {})
            .subscribe(data => {
              if (!this.errorService.manageError(data)) {
                this.loadData();
              }
            });
        }
      })
      .catch(() => null);
  }

  onCancelDeleteAccount() {
    this.apiLavandierService.putUserIdAnonymiseCancel(this.userProfileData.id, {})
      .subscribe(data => {
        if (!this.errorService.manageError(data)) {
          this.loadData();
        }
      });
  }

  onAddPickUp(orderTransportData) {
  }

  onOpenIntercom() {
    window.open(`/user-profile/${this.userProfileData.id}/intercom`, '_blank');
  }

  onOrderKit(orderKit: IKitOrderPayload) {
    this.apiLavandierService.postOrderKit({
      free: orderKit.free,
      userId: this.userProfileData.id,
      houseId: orderKit.shippingHouseId,
      kitList: orderKit.kitList,
    }).subscribe((order) => this.navigateToOrderDetails(order.order.id));
  }

  onOrderKitBatch(orderKitBatch) {
    this.apiLavandierService.postOrderKitBatch({
      atHome: {
        userId: this.userProfileData.id,
        houseId: orderKitBatch.shippingHouseId,
      },
      kitList: orderKitBatch.kitList,
    })
      .subscribe((data: { order: IOrder }) => this.navigateToOrderDetails(data.order.id));
  }

  private navigateToOrderDetails(orderId: number) {
    this.router.navigate(['order-details', orderId]);
  }
}
